import { ChangeDetectorRef, Injectable, NgZone } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { take } from "rxjs/operators";

import { UtilsService } from "./utils/utils.service";

declare type HammerInput = any;

@Injectable({ providedIn: "root" })
export class TouchZoomService {
  // Public properties
  public touchScale = 1;
  public actualTouchZoom: number = null;
  public isTransforming = false;
  public canvasLoading = false;

  // Private properties
  private _canvas: HTMLCanvasElement;
  private _container: HTMLElement;
  private _imageUrl: string;
  private _changeDetectorRef: ChangeDetectorRef;
  private _imageBitmap: ImageBitmap = null;
  private _canvasImage: HTMLImageElement;
  private _canvasContext: CanvasRenderingContext2D;
  private _canvasContainerDimensions: { width: number; height: number; centerX: number; centerY: number };
  private _canvasImageDimensions: { width: number; height: number };
  private _rafId: number;
  private _lastTouchScale = 1;
  private _touchScaleOffset = { x: 0, y: 0 };
  private _lastTouchScaleOffset = { x: 0, y: 0 };
  private _touchScaleStartPoint = { x: 0, y: 0 };
  private _baseTouchScale: number;
  private _pinchLastTime = 0;
  private _animationFrame: number = null;
  private _lastTransform: string = null;
  private _firstRenderSubject = new BehaviorSubject<boolean>(false);
  readonly firstRender$ = this._firstRenderSubject.asObservable();
  private _downsampledBitmapLow: ImageBitmap = null; // Lower resolution for initial view
  private _downsampledBitmapMedium: ImageBitmap = null; // Medium resolution for intermediate zoom
  private readonly FRAME_INTERVAL = 1000 / 120; // 120 FPS
  private readonly PIXEL_THRESHOLD = 8192 * 8192;
  private _panVelocity = { x: 0, y: 0 }; // Velocity for momentum effect
  private _lastPanTime = 0; // Time tracking for velocity calculation
  private _lastPanPosition = { x: 0, y: 0 }; // Position tracking for velocity calculation
  private readonly MOMENTUM_FRICTION = 0.965; // Friction coefficient for slowing down momentum (0.95 = 5% slowdown
  // per frame)
  private readonly MOMENTUM_THRESHOLD = 0.005; // Minimum velocity to continue momentum
  // Configuration
  private _naturalWidth: number;
  private _naturalHeight: number;
  private _maxZoom = 8;

  constructor(
    private readonly utilsService: UtilsService,
    private readonly ngZone: NgZone
  ) {}

  private get isVeryLargeImage(): boolean {
    return this._naturalWidth * (this._naturalHeight || this._naturalWidth) > this.PIXEL_THRESHOLD;
  }

  public init(
    canvas: HTMLCanvasElement,
    container: HTMLElement,
    imageUrl: string,
    changeDetectorRef: ChangeDetectorRef,
    naturalWidth?: number,
    naturalHeight?: number,
    maxZoom?: number
  ): void {
    // Reset any previous state first to ensure clean initialization
    this.reset();

    this._canvas = canvas;
    this._container = container;
    this._imageUrl = imageUrl;
    this._changeDetectorRef = changeDetectorRef;

    if (naturalWidth) {
      this._naturalWidth = naturalWidth;
    }

    if (naturalHeight) {
      this._naturalHeight = naturalHeight;
    }

    if (maxZoom) {
      this._maxZoom = maxZoom;
    }

    // Extra validation to ensure we have valid canvas and container
    if (!this._canvas || !this._container) {
      return;
    }

    // Initialize immediately - delays caused flickering issues
    this._initCanvas();
  }

  public updateDimensions(canvas: HTMLCanvasElement, container: HTMLElement): void {
    // Store previous context if it exists
    const hadContext = !!this._canvasContext;

    this._canvas = canvas;
    this._container = container;

    // Reacquire canvas context to ensure it's valid after dimension changes
    if (this._canvas) {
      try {
        this._canvasContext = this._canvas.getContext("2d", {
          alpha: false,
          willReadFrequently: false
        });
      } catch (error) {
        console.error("Failed to get canvas context after dimension update", error);
      }
    }

    // Reset cached transform to force redraw with new dimensions
    this._lastTransform = null;

    // Reset zoom and pan state to prevent incorrect positioning after rotation
    this._resetTouchZoom();

    this._updateCanvasDimensions();
    this._drawCanvas();
  }

  public onPinchStart(event: HammerInput): void {
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;

      // Reset momentum if it was active
      this._panVelocity = { x: 0, y: 0 };
    }

    this._lastTouchScale = this.touchScale;

    // Calculate pinch center relative to container
    const rect = this._container.getBoundingClientRect();
    this._touchScaleStartPoint = {
      x: event.center.x - rect.left,
      y: event.center.y - rect.top
    };

    // Store the current offset
    this._lastTouchScaleOffset = { ...this._touchScaleOffset };

    this.isTransforming = true;
  }

  public onPinchMove(event: HammerInput): void {
    if (event.timeStamp - this._pinchLastTime < this.FRAME_INTERVAL) {
      return;
    }
    this._pinchLastTime = event.timeStamp;

    const naturalScale = this._canvasContainerDimensions.width / this._naturalWidth;
    const maxScale = this.isVeryLargeImage ? 1 : this._maxZoom / naturalScale;
    const newScale = Math.min(Math.max(this._lastTouchScale * event.scale, 1), maxScale);

    // Get current pinch center using cached container offset
    const currentCenter = {
      x: event.center.x,
      y: event.center.y
    };

    // Calculate the translation (pan)
    const deltaX = (currentCenter.x - this._touchScaleStartPoint.x) / this.touchScale;
    const deltaY = (currentCenter.y - this._touchScaleStartPoint.y) / this.touchScale;

    if (newScale !== this.touchScale) {
      // Convert pinch center to image space coordinates
      const imageSpaceX =
        (this._touchScaleStartPoint.x - this._canvasContainerDimensions.centerX) / this.touchScale +
        this._touchScaleOffset.x;
      const imageSpaceY =
        (this._touchScaleStartPoint.y - this._canvasContainerDimensions.centerY) / this.touchScale +
        this._touchScaleOffset.y;

      // Calculate new offsets to maintain the pinch center point
      this._touchScaleOffset = {
        x: imageSpaceX - (imageSpaceX - this._touchScaleOffset.x) * (newScale / this.touchScale) + deltaX,
        y: imageSpaceY - (imageSpaceY - this._touchScaleOffset.y) * (newScale / this.touchScale) + deltaY
      };

      this.touchScale = newScale;
      this._updateActualTouchZoom();
    } else {
      // Just apply the translation if scale hasn't changed
      this._touchScaleOffset = {
        x: this._touchScaleOffset.x + deltaX,
        y: this._touchScaleOffset.y + deltaY
      };
    }

    // Update the start point for the next move event
    this._touchScaleStartPoint = currentCenter;

    // Ensure offsets stay within bounds
    this._clampOffset();
    this._drawCanvas();
  }

  public onPinchEnd(): void {
    this._lastTouchScale = this.touchScale;
    if (this.touchScale <= 1) {
      this._resetTouchZoom();
    }

    this.isTransforming = false;
    this._drawCanvas();
  }

  public onPanStart(event: HammerInput): void {
    // Cancel any ongoing animations first
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }

    // Only enable panning when zoomed in
    if (this.touchScale <= 1) {
      return;
    }

    // Store the current position as the reference point for this pan
    // Make a deep copy to prevent reference issues
    this._lastTouchScaleOffset = {
      x: this._touchScaleOffset.x,
      y: this._touchScaleOffset.y
    };

    // Store the starting point of this pan gesture
    this._touchScaleStartPoint = {
      x: event.center.x,
      y: event.center.y
    };

    // Initialize velocity tracking
    this._panVelocity = { x: 0, y: 0 };
    this._lastPanTime = event.timeStamp;
    this._lastPanPosition = {
      x: event.center.x,
      y: event.center.y
    };

    // Mark that we're in an active transform operation
    this.isTransforming = true;
  }

  public onPanMove(event: HammerInput): void {
    // Only process panning when zoomed in and not too frequently (for performance)
    if (this.touchScale <= 1) {
      return;
    }

    // Calculate delta from the starting point of this pan gesture
    const deltaX = event.center.x - this._touchScaleStartPoint.x;
    const deltaY = event.center.y - this._touchScaleStartPoint.y;

    // Get the max allowed offsets at current scale
    const { width, height } = this._canvasContainerDimensions;
    const { width: drawWidth, height: drawHeight } = this._canvasImageDimensions;

    const scaledWidth = drawWidth * this.touchScale;
    const scaledHeight = drawHeight * this.touchScale;

    const maxOffsetX = Math.max(0, (scaledWidth - width) / (2 * this.touchScale));
    const maxOffsetY = Math.max(0, (scaledHeight - height) / (2 * this.touchScale));

    // Calculate new position by applying the delta directly to the initial pan position
    // Divide by scale to ensure consistent movement at different zoom levels
    const adjustedDeltaX = deltaX / this.touchScale;
    const adjustedDeltaY = deltaY / this.touchScale;

    // Apply the movement to the starting position of this pan
    this._touchScaleOffset = {
      x: this._lastTouchScaleOffset.x + adjustedDeltaX,
      y: this._lastTouchScaleOffset.y + adjustedDeltaY
    };

    // Apply bounds checking to keep the image within view
    this._touchScaleOffset.x = Math.min(Math.max(this._touchScaleOffset.x, -maxOffsetX), maxOffsetX);
    this._touchScaleOffset.y = Math.min(Math.max(this._touchScaleOffset.y, -maxOffsetY), maxOffsetY);

    // Update velocity tracking for momentum effect
    const timeDelta = event.timeStamp - this._lastPanTime;
    if (timeDelta > 0) {
      // Calculate velocity in pixels per millisecond
      this._panVelocity = {
        x: (event.center.x - this._lastPanPosition.x) / timeDelta / this.touchScale,
        y: (event.center.y - this._lastPanPosition.y) / timeDelta / this.touchScale
      };

      // Update last position and time for next calculation
      this._lastPanPosition = {
        x: event.center.x,
        y: event.center.y
      };
      this._lastPanTime = event.timeStamp;
    }

    // Draw the canvas with updated position
    this._drawCanvas();
  }

  public onPanEnd(event: HammerInput): void {
    if (this.touchScale <= 1) {
      return;
    }

    // Update the last touch scale offset to the current position
    this._lastTouchScaleOffset = {
      x: this._touchScaleOffset.x,
      y: this._touchScaleOffset.y
    };

    // End the active transformation state
    this.isTransforming = false;

    // Apply momentum effect if velocity is significant
    if (
      Math.abs(this._panVelocity.x) > this.MOMENTUM_THRESHOLD ||
      Math.abs(this._panVelocity.y) > this.MOMENTUM_THRESHOLD
    ) {
      this._applyPanMomentum();
    } else {
      // Just draw one final time with no momentum
      this._drawCanvas();
    }
  }

  private _applyPanMomentum(): void {
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
    }

    // Start a new animation frame loop for the momentum effect
    const animate = () => {
      // Calculate the max allowed offsets at current scale
      const { width, height } = this._canvasContainerDimensions;
      const { width: drawWidth, height: drawHeight } = this._canvasImageDimensions;

      const scaledWidth = drawWidth * this.touchScale;
      const scaledHeight = drawHeight * this.touchScale;

      const maxOffsetX = Math.max(0, (scaledWidth - width) / (2 * this.touchScale));
      const maxOffsetY = Math.max(0, (scaledHeight - height) / (2 * this.touchScale));

      // Calculate the new position using the current velocity
      const newOffsetX = this._touchScaleOffset.x + this._panVelocity.x * 16; // Assuming ~60fps (16ms per frame)
      const newOffsetY = this._touchScaleOffset.y + this._panVelocity.y * 16;

      // Apply bounds checking
      const wasAtBoundaryX = this._touchScaleOffset.x === -maxOffsetX || this._touchScaleOffset.x === maxOffsetX;
      const wasAtBoundaryY = this._touchScaleOffset.y === -maxOffsetY || this._touchScaleOffset.y === maxOffsetY;

      this._touchScaleOffset.x = Math.min(Math.max(newOffsetX, -maxOffsetX), maxOffsetX);
      this._touchScaleOffset.y = Math.min(Math.max(newOffsetY, -maxOffsetY), maxOffsetY);

      // Check if we hit a boundary after this move
      const hitBoundaryX =
        (this._touchScaleOffset.x === -maxOffsetX || this._touchScaleOffset.x === maxOffsetX) && !wasAtBoundaryX;
      const hitBoundaryY =
        (this._touchScaleOffset.y === -maxOffsetY || this._touchScaleOffset.y === maxOffsetY) && !wasAtBoundaryY;

      // If we hit a boundary, reverse and dampen that component of velocity
      if (hitBoundaryX) {
        this._panVelocity.x = -this._panVelocity.x * 0.2; // Bounce with 80% energy loss
      }

      if (hitBoundaryY) {
        this._panVelocity.y = -this._panVelocity.y * 0.2; // Bounce with 80% energy loss
      }

      // Apply friction to gradually slow down the movement
      this._panVelocity.x *= this.MOMENTUM_FRICTION;
      this._panVelocity.y *= this.MOMENTUM_FRICTION;

      // Update the last touch scale offset to match current position
      this._lastTouchScaleOffset = {
        x: this._touchScaleOffset.x,
        y: this._touchScaleOffset.y
      };

      // Draw with the new position
      this._drawCanvas();

      // Continue the animation if velocity is still significant
      if (
        Math.abs(this._panVelocity.x) > this.MOMENTUM_THRESHOLD ||
        Math.abs(this._panVelocity.y) > this.MOMENTUM_THRESHOLD
      ) {
        this._animationFrame = requestAnimationFrame(animate);
      } else {
        // Final cleanup when momentum is complete
        this._animationFrame = null;
        this._panVelocity = { x: 0, y: 0 };
      }
    };

    // Start the animation
    this._animationFrame = requestAnimationFrame(animate);
  }

  public onDoubleTap(event: HammerInput): void {
    if (this.touchScale > 1) {
      this._animateZoom(1, 0, 0);
    } else {
      const displayWidth = this._container.offsetWidth;
      this._baseTouchScale = displayWidth / this._naturalWidth;
      const targetScale = 1 / this._baseTouchScale; // This will make actualTouchZoom = 1

      const rect = this._container.getBoundingClientRect();
      const x = event.center.x - rect.left;
      const y = event.center.y - rect.top;

      const targetOffsetX = rect.width / 2 - x;
      const targetOffsetY = rect.height / 2 - y;

      this._animateZoom(targetScale, targetOffsetX, targetOffsetY);
    }
  }

  public reset(): void {
    this._resetTouchZoom();
  }

  public destroy(): void {
    // First reset all state
    this._resetTouchZoom();

    // Clean up bitmaps
    if (this._imageBitmap) {
      this._imageBitmap.close();
      this._imageBitmap = null;
    }

    if (this._downsampledBitmapLow) {
      this._downsampledBitmapLow.close();
      this._downsampledBitmapLow = null;
    }

    if (this._downsampledBitmapMedium) {
      this._downsampledBitmapMedium.close();
      this._downsampledBitmapMedium = null;
    }

    // Cancel any pending animation frames
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }

    // Reset momentum tracking values
    this._panVelocity = { x: 0, y: 0 };
    this._lastPanTime = 0;
    this._lastPanPosition = { x: 0, y: 0 };

    // Reset initialization tracking
    this._initCanvasRetryCount = 0;

    // Reset canvas and image references
    this._canvasContext = null;
    this._canvasImage = null;
    this._canvas = null;
    this._container = null;

    // Reset loading state
    this.canvasLoading = false;

    // Reset dimensions
    this._canvasContainerDimensions = null;
    this._canvasImageDimensions = null;

    // Reset first render state
    this._firstRenderSubject.next(false);
  }

  private _initCanvas(): void {
    if (!this._canvas) {
      if (!this._initCanvasRetryCount) {
        this._initCanvasRetryCount = 0;
      }

      if (this._initCanvasRetryCount < 10) {
        // Max 10 retries
        this._initCanvasRetryCount++;

        this.utilsService.delay(100).subscribe(() => {
          // Increased delay for better canvas availability
          this._initCanvas();
          this._changeDetectorRef.markForCheck();
        });
      } else {
        this._initCanvasRetryCount = 0;
      }
      return;
    }

    // Reset retry counter on successful initialization
    this._initCanvasRetryCount = 0;

    if (this.canvasLoading) {
      return;
    }

    this.canvasLoading = true;
    this._canvasImage = new Image();
    this._canvasImage.decoding = "async";

    // Pre-initialize canvas context to prevent flickering
    if (this._canvas) {
      this._canvasContext = this._canvas.getContext("2d", {
        alpha: false,
        willReadFrequently: false
      });

      // Pre-initialize with black background
      if (this._canvasContext) {
        this._canvasContext.fillStyle = "#000000";
        this._canvasContext.fillRect(0, 0, this._canvas.width, this._canvas.height);
      }
    }

    this._canvasImage.onload = () => {
      try {
        // Store natural dimensions if not provided
        if (!this._naturalWidth) {
          this._naturalWidth = this._canvasImage.naturalWidth;
        }

        if (!this._naturalHeight) {
          this._naturalHeight = this._canvasImage.naturalHeight;
        }

        // Create both full resolution and downsampled bitmaps
        const fullResBitmapPromise = createImageBitmap(this._canvasImage, {
          resizeQuality: "high",
          premultiplyAlpha: "premultiply"
        });

        const canvas = this._canvas;
        this._canvasContext = canvas.getContext("2d", {
          alpha: false,
          willReadFrequently: false
        });

        this._updateCanvasDimensions();

        // First ensure we have the full resolution bitmap
        fullResBitmapPromise
          .then(fullBitmap => {
            this._imageBitmap = fullBitmap;

            // Now try to create the downsampled bitmaps, but handle failure gracefully
            this._createDownsampledBitmaps()
              .then(([lowResBitmap, medResBitmap]) => {
                this._downsampledBitmapLow = lowResBitmap;
                this._downsampledBitmapMedium = medResBitmap;
              })
              .catch(error => {
                // Continue with only the full resolution bitmap
              })
              .finally(() => {
                // Draw the canvas regardless of whether downsampling succeeded
                this._drawCanvas();

                this.firstRender$.pipe(take(1)).subscribe(() => {
                  this.canvasLoading = false;
                  this._changeDetectorRef.markForCheck();
                });

                this._changeDetectorRef.markForCheck();
              });
          })
          .catch(error => {
            // Failed to create any bitmap, but attempt to draw with fallback
            console.error("Failed to create bitmap, attempting fallback rendering", error);
            this._drawCanvas();
            this.firstRender$.pipe(take(1)).subscribe(() => {
              this.canvasLoading = false;
              this._changeDetectorRef.markForCheck();
            });
            this._changeDetectorRef.markForCheck();
          });
      } catch (error) {
        // Failed to initialize canvas
        console.error("Failed to initialize canvas", error);
        this.canvasLoading = false;
        this._changeDetectorRef.markForCheck();
        this.canvasLoading = false;
      }
    };

    this._canvasImage.onerror = error => {
      console.error("Error loading image for canvas", error);
      this.canvasLoading = false;
      this._changeDetectorRef.markForCheck();

      // Notify observers that initialization failed
      this._firstRenderSubject.next(false);
    };

    this._canvasImage.src = this._imageUrl;
  }

  // Track retry attempts
  private _initCanvasRetryCount = 0;

  private _updateCanvasDimensions(): void {
    if (!this._container || !this._canvas) {
      return;
    }

    const container = this._container;
    const canvas = this._canvas;

    this._canvasContainerDimensions = {
      width: container.offsetWidth,
      height: container.offsetHeight,
      centerX: container.offsetWidth * 0.5,
      centerY: container.offsetHeight * 0.5
    };

    // Only update canvas dimensions if they've changed
    // This helps prevent unnecessary redraws that can cause flickering
    if (
      canvas.width !== this._canvasContainerDimensions.width ||
      canvas.height !== this._canvasContainerDimensions.height
    ) {
      canvas.width = this._canvasContainerDimensions.width;
      canvas.height = this._canvasContainerDimensions.height;

      // If we just changed the canvas size, ensure it has a black background
      if (this._canvasContext) {
        this._canvasContext.fillStyle = "#000000";
        this._canvasContext.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Recalculate image dimensions using natural dimensions
    // Use natural dimensions if available, otherwise fall back to canvas image dimensions
    const imageWidth = this._naturalWidth || this._canvasImage?.naturalWidth || this._canvasImage?.width || 1;
    const imageHeight = this._naturalHeight || this._canvasImage?.naturalHeight || this._canvasImage?.height || 1;
    const imageRatio = imageWidth / imageHeight;
    const containerRatio = this._canvasContainerDimensions.width / this._canvasContainerDimensions.height;

    this._canvasImageDimensions = {
      width:
        imageRatio > containerRatio
          ? this._canvasContainerDimensions.width
          : this._canvasContainerDimensions.height * imageRatio,
      height:
        imageRatio > containerRatio
          ? this._canvasContainerDimensions.width / imageRatio
          : this._canvasContainerDimensions.height
    };
  }

  private _createDownsampledBitmaps(): Promise<[ImageBitmap, ImageBitmap]> {
    if (!this._canvasImage || !this._canvasContainerDimensions) {
      return Promise.resolve([null, null]);
    }

    const originalWidth = this._canvasImage.width;
    const originalHeight = this._canvasImage.height;

    // Create a low-resolution bitmap (2x display size)
    // This is used for the initial view with minimal memory usage
    const lowResWidth = Math.min(this._canvasContainerDimensions.width * 2, originalWidth);
    const lowResHeight = Math.round(originalHeight * (lowResWidth / originalWidth));

    // Create a medium-resolution bitmap (6x display size or half original, whichever is smaller)
    // This is used for intermediate zoom levels
    const medResWidth = Math.min(this._canvasContainerDimensions.width * 6, originalWidth);
    const medResHeight = Math.round(originalHeight * (medResWidth / originalWidth));

    // Create both downsampled bitmaps
    const lowBitmapPromise = this._createSingleDownsampledBitmap(lowResWidth, lowResHeight);
    const medBitmapPromise = this._createSingleDownsampledBitmap(medResWidth, medResHeight);

    // Return both promises together
    return Promise.all([lowBitmapPromise, medBitmapPromise]);
  }

  private _createSingleDownsampledBitmap(width: number, height: number): Promise<ImageBitmap> {
    // Create an offscreen canvas for downsampling
    const offscreenCanvas = new OffscreenCanvas(width, height);
    const offscreenCtx = offscreenCanvas.getContext("2d") as OffscreenCanvasRenderingContext2D;

    // Apply smoothing settings for better downsampling
    offscreenCtx.imageSmoothingEnabled = true;
    offscreenCtx.imageSmoothingQuality = "high";

    // Draw the image at the target size
    offscreenCtx.drawImage(this._canvasImage, 0, 0, width, height);

    // Return a bitmap from this intermediate canvas
    return createImageBitmap(offscreenCanvas, {
      resizeQuality: "high",
      premultiplyAlpha: "premultiply"
    });
  }

  private _drawCanvas(): void {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = requestAnimationFrame(this._performDrawCanvas.bind(this));
  }

  private _performDrawCanvas(): void {
    // Ensure we have a canvas context and at least one valid bitmap
    if (!this._canvasContext) {
      return;
    }

    // If we don't have any bitmap available, there's nothing to draw
    if (!this._imageBitmap && !this._downsampledBitmapLow && !this._downsampledBitmapMedium) {
      return;
    }

    // Ensure we have valid dimensions
    if (!this._canvasContainerDimensions || !this._canvasImageDimensions) {
      return;
    }

    const ctx = this._canvasContext;
    const { width, height, centerX, centerY } = this._canvasContainerDimensions;
    const { width: drawWidth, height: drawHeight } = this._canvasImageDimensions;

    // Cache transform matrix
    const transform = `${this.touchScale},${centerX + this._touchScaleOffset.x * this.touchScale},${
      centerY + this._touchScaleOffset.y * this.touchScale
    }`;

    if (this._lastTransform === transform) {
      return;
    }
    this._lastTransform = transform;

    // Apply image smoothing settings for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // When zoomed in beyond 1:1 pixel mapping, disable smoothing for crisp pixels
    if (this.touchScale > 1 && this.touchScale > this._naturalWidth / this._canvasImageDimensions.width) {
      ctx.imageSmoothingEnabled = false;
    }

    // Do not clear the canvas if we're drawing for the first time (prevents flash of blank canvas)
    if (this._lastTransform !== null) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#000000"; // Solid black background
      ctx.fillRect(0, 0, width, height);
    } else {
      // We need to set the transform to identity to apply the next transform correctly
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    ctx.setTransform(
      this.touchScale,
      0,
      0,
      this.touchScale,
      centerX + this._touchScaleOffset.x * this.touchScale,
      centerY + this._touchScaleOffset.y * this.touchScale
    );

    this._clampOffset();

    // Calculate the current display pixels needed based on zoom
    // This represents how many actual source pixels we need to display without quality loss
    const displayWidth = drawWidth * this.touchScale;

    // Select the appropriate bitmap based on which resolution is most efficient for current zoom
    let bitmapToUse: ImageBitmap;

    // Helper function to avoid choosing a bitmap that would need to be stretched beyond 100%
    const isBitmapSufficientForDisplay = (bitmap: ImageBitmap): boolean => {
      if (!bitmap) {
        return false;
      }
      // Return true if the bitmap has enough resolution for the current display size
      return bitmap.width >= displayWidth * 1.1; // Buffer for quality
    };

    // First try low-res bitmap if it has enough resolution
    if (this._downsampledBitmapLow && isBitmapSufficientForDisplay(this._downsampledBitmapLow)) {
      bitmapToUse = this._downsampledBitmapLow;
    }
    // Then try medium-res bitmap if it has enough resolution
    else if (this._downsampledBitmapMedium && isBitmapSufficientForDisplay(this._downsampledBitmapMedium)) {
      bitmapToUse = this._downsampledBitmapMedium;
    }
    // Finally use full-res bitmap
    else if (this._imageBitmap) {
      bitmapToUse = this._imageBitmap;
    }

    // Fallback chain if the preferred bitmap is not available
    if (!bitmapToUse) {
      bitmapToUse = this._imageBitmap || this._downsampledBitmapMedium || this._downsampledBitmapLow;
    }

    // Only draw if we have a valid bitmap to use
    if (bitmapToUse) {
      try {
        ctx.drawImage(bitmapToUse, -drawWidth * 0.5, -drawHeight * 0.5, drawWidth, drawHeight);

        // Signal successful drawing - only on first successful draw
        if (!this._firstRenderSubject.value) {
          this._firstRenderSubject.next(true);
          this._changeDetectorRef.markForCheck();
        }
      } catch (error) {
        console.error("Error drawing bitmap to canvas", error);
        // Don't set firstRender to true if drawing failed
      }
    } else {
      // If we can't draw due to missing bitmap but everything else is ready,
      // schedule another attempt after a short delay
      this.ngZone.runOutsideAngular(() => {
        this.utilsService.delay(50).subscribe(() => this._drawCanvas());
      });
    }
  }

  private _clampOffset(): void {
    const { width, height } = this._canvasContainerDimensions;
    const { width: drawWidth, height: drawHeight } = this._canvasImageDimensions;

    const scaledWidth = drawWidth * this.touchScale;
    const scaledHeight = drawHeight * this.touchScale;
    const maxOffsetX = Math.max(0, (scaledWidth - width) / (2 * this.touchScale));
    const maxOffsetY = Math.max(0, (scaledHeight - height) / (2 * this.touchScale));

    this._touchScaleOffset = {
      x: Math.min(Math.max(this._touchScaleOffset.x, -maxOffsetX), maxOffsetX),
      y: Math.min(Math.max(this._touchScaleOffset.y, -maxOffsetY), maxOffsetY)
    };
  }

  private _updateActualTouchZoom(): void {
    if (!this._naturalWidth || !this._container?.offsetWidth) {
      this.actualTouchZoom = null;
      return;
    }

    const displayWidth = this._container.offsetWidth;
    this._baseTouchScale = displayWidth / this._naturalWidth;
    this.actualTouchZoom = this._baseTouchScale * this.touchScale;
  }

  private _animateZoom(targetScale: number, targetOffsetX = 0, targetOffsetY = 0): void {
    const startScale = this.touchScale;
    const startOffsetX = this._touchScaleOffset.x;
    const startOffsetY = this._touchScaleOffset.y;
    const duration = 300; // milliseconds
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic function
      const easing = 1 - Math.pow(1 - progress, 3);

      this.touchScale = startScale + (targetScale - startScale) * easing;
      this._touchScaleOffset.x = startOffsetX + (targetOffsetX - startOffsetX) * easing;
      this._touchScaleOffset.y = startOffsetY + (targetOffsetY - startOffsetY) * easing;

      this._updateActualTouchZoom();
      this._changeDetectorRef.markForCheck();
      this._drawCanvas();

      if (progress < 1) {
        this._animationFrame = requestAnimationFrame(animate);
      } else {
        this._lastTouchScale = this.touchScale;
        this._lastTouchScaleOffset = { ...this._touchScaleOffset };
      }
    };

    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
    }
    this._animationFrame = requestAnimationFrame(animate);
  }

  private _resetTouchZoom(): void {
    this.touchScale = 1;
    this._lastTouchScale = 1;
    this._touchScaleOffset = { x: 0, y: 0 };
    this._lastTouchScaleOffset = { x: 0, y: 0 };
    this._touchScaleStartPoint = { x: 0, y: 0 };
    this._pinchLastTime = 0;
    this._lastTransform = null;
    this.isTransforming = false;

    // Reset momentum values
    this._panVelocity = { x: 0, y: 0 };
    this._lastPanTime = 0;
    this._lastPanPosition = { x: 0, y: 0 };

    // Cancel any ongoing animations
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }

    this._updateActualTouchZoom();

    // Only try to draw if we have a canvas context
    if (this._canvasContext) {
      this._drawCanvas();
    }
  }
}
